import { Button, Intent, Tooltip, Position } from '@blueprintjs/core';
import { inject, observer } from 'mobx-react';
import { Instance } from 'mobx-state-tree';
import React from 'react';
import { Page, TLCode } from '../../common/enums';
import {
  PharmacyDetailedStore,
  PharmacyStore,
  GroupStore,
  AnchorStore,
  PharmacyStatusStore,
  ApplicationStore,
} from '../../store';
import { Pharmacy, Group } from '../../store/models';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import styled from '@emotion/styled';
import { IconNames } from '@blueprintjs/icons';
import {
  DataGrid,
  Column,
  Button as DGButton,
  Sorting,
  SearchPanel,
  DataGridTypes,
  Export,
  FilterRow,
  FilterPanel,
  HeaderFilter,
  GroupPanel,
  Grouping,
  ColumnChooser,
  Pager,
  Paging,
} from 'devextreme-react/data-grid';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { PharmacyStatusCell } from '../common/grid/PharmacyStatusCell';
import { RefreshGeoDataDialog } from '../common/dialog/RefreshGeoDataDialog';

const Container = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: start;
  gap: 20px;
  align-content: center;
`;

const DivColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const DivRightColumn = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: auto;
`;

interface GroupPharmaciesTabProps extends WithTranslateProps {
  readOnly: boolean;
  group?: Instance<typeof Group>;
  ApplicationStore?: Instance<typeof ApplicationStore>;
  PharmacyStore?: Instance<typeof PharmacyStore>;
  PharmacyDetailedStore?: Instance<typeof PharmacyDetailedStore>;
  GroupStore?: Instance<typeof GroupStore>;
  AnchorStore?: Instance<typeof AnchorStore>;
  PharmacyStatusStore?: Instance<typeof PharmacyStatusStore>;
  //refreshGroupPharmacyGeodata: (groupId: number) => void;
}

interface GroupPharmaciesTabState {
  isLoading: boolean;
  pharmaciesToLink: Array<Instance<typeof Pharmacy>>;
  refreshGeoDataDialogDone: boolean;
  refreshGeoDataDialoginProgress: boolean;
  refreshGeoDataDialoginError: boolean;
}

@inject('ApplicationStore')
@inject('PharmacyStore')
@inject('PharmacyDetailedStore')
@inject('GroupStore')
@inject('AnchorStore')
@inject('PharmacyStatusStore')
@observer
class GroupPharmaciesTab extends React.Component<GroupPharmaciesTabProps, GroupPharmaciesTabState> {
  state = {
    isLoading: false,
    pharmaciesToLink: [],
    refreshGeoDataDialogDone: false,
    refreshGeoDataDialoginProgress: false,
    refreshGeoDataDialoginError: false,
  } as GroupPharmaciesTabState;

  _isMounted = false;

  async componentDidMount() {
    this._isMounted = true;
    this._isMounted && this.setState({ isLoading: true });
    await this.props.GroupStore!.checkListLoaded();
    await this.props.AnchorStore!.checkListLoaded();
    await this.props.PharmacyStore!.checkListLoaded();
    this._isMounted && this.setState({ isLoading: false });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  // private pharmaciesWithoutSecretariat = () => {
  //   return this.props
  //     .PharmacyStore!.dataList.filter((elem) => {
  //       return elem.secretariatId === undefined;
  //     })
  //     .filter((elem) => this.state.pharmaciesToLink.findIndex((p) => p.pharmacyId === elem.pharmacyId) === -1);
  // };

  // private pharmaciesItemRenderer = (
  //   item: Instance<typeof Pharmacy>,
  //   { handleClick, modifiers }: IItemRendererProps,
  // ) => {
  //   if (!modifiers.matchesPredicate) return null;
  //   return (
  //     <MenuItem
  //       active={modifiers.active}
  //       key={item.pharmacyId}
  //       onClick={handleClick}
  //       text={item.apbNumber.toString()}
  //     />
  //   );
  // };

  // private onPharmacySelected = (item: Instance<typeof Pharmacy>) => {
  //   this.setState({ pharmaciesToLink: [...this.state.pharmaciesToLink, item] });
  // };

  // private onPharmacyRemoved = (value: string, index: number) => {
  //   this.setState({
  //     pharmaciesToLink: this.state.pharmaciesToLink.filter((elem) => elem.apbNumber.toString() !== value),
  //   });
  // };

  // private linkSelectedPharmaciesToSecretariat = () => {
  //   if (this.props.secretariat !== undefined)
  //     this.state.pharmaciesToLink.forEach(async (element) => {
  //       await this.props.PharmacyDetailedStore!.getByIdWithDetails(element.pharmacyId);
  //       const pharmacyToSave = this.props.PharmacyDetailedStore!.getById(element.pharmacyId);
  //       this.props.PharmacyDetailedStore!.saveElement({
  //         ...pharmacyToSave,
  //         secretariatId: this.props.secretariat!.secretariatId,
  //       });
  //     });
  // };

  private buildGridData = () => {
    return this.props
      .PharmacyStore!.dataList.filter((elem) => {
        return this.props.group !== undefined && elem.groupId === this.props.group!.groupId;
      })
      .map((p) => ({
        pharmacyId: p.pharmacyId,
        apbNumber: p.apbNumber,
        name: p.name,
        address: p.address,
        zipCode: p.zipCode,
        city: p.city,
        level: p.level,
        statusId: p.statusId,
        group:
          p.groupId === undefined ? '' : this.props.GroupStore!.dataList.find((g) => g.groupId === p.groupId)!.name,
        secretariatId: p.secretariatId,
        anchor:
          p.anchorId === undefined
            ? ''
            : this.props.AnchorStore!.dataList.find((a) => a.anchorId === p.anchorId!)!.name,
      }));
  };

  displayPharmacyStatusCell = (params: any) => {
    const pharmacyStatus = this.props.PharmacyStatusStore!.dataList.find(
      (ps) => ps.pharmacyStatusId === params.row.data.statusId,
    );
    if (pharmacyStatus === undefined) return <></>;
    return <PharmacyStatusCell pharmacyStatus={pharmacyStatus} />;
  };

  private refreshGeodata = () => {
    this.setState({ refreshGeoDataDialoginProgress: true });
    const result = (this.props.GroupStore!.refreshGroupPharmacyGeodata(
      this.props.group!.groupId,
    ) as unknown) as boolean;
    if (result) {
      this.setState({ refreshGeoDataDialogDone: true, refreshGeoDataDialoginProgress: false });
    } else {
      this.setState({ refreshGeoDataDialoginError: true, refreshGeoDataDialoginProgress: false });
    }
  };

  private onRefreshGeoDataDialogClosed = () => {
    this.setState({
      refreshGeoDataDialogDone: false,
      refreshGeoDataDialoginProgress: false,
      refreshGeoDataDialoginError: false,
    });
  };

  onExporting = (e: DataGridTypes.ExportingEvent) => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Main sheet');

    exportDataGrid({
      component: e.component,
      worksheet,
      autoFilterEnabled: true,
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(
          new Blob([buffer], { type: 'application/octet-stream' }),
          'Pharmacies_of_group_' + this.props.group!.name + '.xlsx',
        );
      });
    });
  };

  onRowClicked = (ev) => {
    if (ev.data.pharmacy) {
      this.props.ApplicationStore!.navigate(`${Page.PharmacyPage}/${ev.data.pharmacy}`);
    } else if (ev.data.pharmacyId) {
      this.props.ApplicationStore!.navigate(`${Page.PharmacyPage}/${ev.data.pharmacyId}`);
    }
  };

    private recalculateDefaultExchangeFlags  = () => {
      this.props.PharmacyStore!.recalculateDefaultExchangeFlagsByGroup(this.props.group!.groupId);
    };
  
    private reapplyDefaultExchangeFlags  = () => {
      this.props.PharmacyStore!.reapplyDefaultExchangeFlagsByGroup(this.props.group!.groupId);
    };
  
    private recalculateDefaultPairingFlags  = () => {
      this.props.PharmacyStore!.recalculateDefaultPairingFlagsByGroup(this.props.group!.groupId);
    };
  
    private reapplyDefaultPairingFlags  = () => {
      this.props.PharmacyStore!.reapplyDefaultPairingFlagsByGroup(this.props.group!.groupId);
    };
  

  render() {
    const { translate } = this.props;
    const { refreshGeoDataDialogDone, refreshGeoDataDialoginProgress, refreshGeoDataDialoginError } = this.state;
    return (
      <>
        <RefreshGeoDataDialog
          translate={this.props.translate}
          done={refreshGeoDataDialogDone}
          inError={refreshGeoDataDialoginError}
          inProgress={refreshGeoDataDialoginProgress}
          onClose={this.onRefreshGeoDataDialogClosed}
        />
        <Container>
          <DivLine>
            {!this.props.readOnly ? (
              <>
                <DivColumn>
                  <DivLine />
                </DivColumn>
              </>
            ) : (
              <></>
            )}
            <DivRightColumn>
              <DivLine>
                      <DivColumn>
                      <div style={{ display:'flex', paddingTop:'5px'}}>
                      <b>{translate(TLCode.PHARMACY_RecalculateExchangeAllowed_Lbl)}</b>&nbsp;
                      {/* <ButtonGroup> */}
                          <Tooltip
                              content={translate(TLCode.PHARMACY_RecalculateDefaultExchangeFlags_ToolTip)}
                              position={Position.BOTTOM}
                              hoverOpenDelay={500}
                          >
                            <Button
                              intent={Intent.PRIMARY}
                              onClick={this.recalculateDefaultExchangeFlags}
                              text={translate(TLCode.PHARMACY_RecalculateDefaultExchangeFlags_Btn)}
                            />
                          </Tooltip>
                          &nbsp;&nbsp;
                          <Tooltip
                              content={translate(TLCode.PHARMACY_ReapplyDefaultExchangeFlags_ToolTip)}
                              position={Position.BOTTOM}
                              hoverOpenDelay={500}
                          >
                          <Button
                            intent={Intent.DANGER}
                            onClick={this.reapplyDefaultExchangeFlags}
                            text={translate(TLCode.PHARMACY_ReapplyDefaultExchangeFlags_Btn)}
                          />
                        </Tooltip>
                      {/* </ButtonGroup> */}
                      </div>
                      </DivColumn>
                      <DivColumn>
                      <div style={{ display:'flex', paddingTop:'5px'}}>
                      <b>{translate(TLCode.PHARMACY_RecalculatePairingAllowed_Lbl)}</b>&nbsp;
                      {/* <ButtonGroup> */}
                      <Tooltip
                        content={translate(TLCode.PHARMACY_RecalculateDefaultPairingFlags_ToolTip)}
                        position={Position.BOTTOM}
                        hoverOpenDelay={500}
                      >
                                                  <Button
                                                    intent={Intent.PRIMARY}
                                                    onClick={this.recalculateDefaultPairingFlags}
                                                    text={translate(TLCode.PHARMACY_RecalculateDefaultPairingFlags_Btn)}
                                                  />
                                                </Tooltip>
                                                &nbsp;&nbsp;
                                                <Tooltip
                                                    content={translate(TLCode.PHARMACY_ReapplyDefaultPairingFlags_ToolTip)}
                                                    position={Position.BOTTOM}
                                                    hoverOpenDelay={500}
                                                >
                                                <Button
                                                  intent={Intent.DANGER}
                                                  onClick={this.reapplyDefaultPairingFlags}
                                                  text={translate(TLCode.PHARMACY_ReapplyDefaultExchangeFlags_Btn)}
                                                />
                                              </Tooltip>
                                            {/* </ButtonGroup> */}
                                            </div>
                                            </DivColumn>
            <DivColumn>
              <Button
                text={translate(TLCode.GROUP_RefreshGeodata)}
                icon={IconNames.REFRESH}
                intent={Intent.DANGER}
                onClick={this.refreshGeodata}
                disabled={this.props.readOnly}
              />
              </DivColumn>
              </DivLine>
            </DivRightColumn>
          </DivLine>
          <br />
          <br />
          <DataGrid
            id="pharmaciesList"
            dataSource={this.buildGridData()}
            showBorders
            onExporting={this.onExporting}
            onRowClick={this.onRowClicked}
            allowColumnResizing
            rowAlternationEnabled={true}
          >
            <Export enabled={true} texts={{ exportAll: translate(TLCode.GRID_EXCEL_EXPORT) }} />
            <Sorting mode="multiple" />
            <FilterPanel visible={true} />
            <HeaderFilter visible={true} />
            <FilterRow visible={true} applyFilter={'auto'} />
            <GroupPanel visible={true} />
            <SearchPanel visible={true} highlightCaseSensitive={true} width={400} />
            <Grouping autoExpandAll={false} />
            <ColumnChooser enabled={true} />
            <Column dataType="number" dataField="level" caption={translate(TLCode.PHARMACIES_Level)} width={40} />
            <Column
              dataType="number"
              dataField="apbNumber"
              caption={translate(TLCode.PHARMACIES_ApbNum)}
              width={80}
              defaultSortOrder="asc"
              sortIndex={1}
            />
            <Column
              dataType="string"
              dataField="name"
              caption={translate(TLCode.PHARMACIES_PharmacyName)}
              width={180}
            />
            <Column dataType="string" dataField="address" caption={translate(TLCode.PHARMACIES_Address)} width={300} />
            <Column dataType="string" dataField="zipCode" caption={translate(TLCode.PHARMACIES_ZipCode)} width={60} />
            <Column dataType="string" dataField="city" caption={translate(TLCode.PHARMACIES_City)} width={180} />
            <Column dataType="string" dataField="group" caption={translate(TLCode.PHARMACIES_Group)} width={120} />
            <Column dataType="string" dataField="anchor" caption={translate(TLCode.PHARMACIES_Anchor)} width={180} />
            <Column
              cellRender={this.displayPharmacyStatusCell}
              caption={translate(TLCode.PHARMACY_Status)}
              width={60}
              alignment="center"
            />
            <Pager
              visible={true}
              allowedPageSizes={[5, 10, 20, 50, 100]}
              showPageSizeSelector={true}
              showInfo={true}
              showNavigationButtons={true}
            />
            <Paging defaultPageSize={20} />
          </DataGrid>
        </Container>
      </>
    );
  }
}

export default withTranslate(GroupPharmaciesTab);
